<script>
import { Bar } from 'vue-chartjs/legacy'
import { Chart as ChartJS, Tooltip as chartTooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import Tooltip from '@components/tooltip.vue'
import _ from 'lodash'
import ProfilePicture from '@components/profile-picture.vue'
ChartJS.register(chartTooltip, Legend, BarElement, CategoryScale, LinearScale)
export default {
  components: { ProfilePicture, Bar, Tooltip },
  props: {
    users: {
      type: Array,
      required: true,
    },
    periods: {
      type: Array,
      required: true,
    },
    office: {
      type: Object,
      required: true,
    },
    loanProductionData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedPeriodIndex: 0,
      allBarPositions: [],
      barWidth: 0,
      chartPlugins: [
        {
          afterDraw: (chart) => {
            const xAxis = chart.scales.x
            this.barWidth = chart.getDatasetMeta(0)?.data[0]?.width
            this.allBarPositions = xAxis.ticks.map((value, index) => {
              return xAxis.getPixelForTick(index)
            })
          },
        },
      ],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            displayColors: false,
            backgroundColor: '#eef1f4',
            bodyColor: '#42464a',
            titleColor: '#42464a',
            callbacks: {
              beforeBody: () => {
                return ''
              },
              label: (context) => {
                const itemIndex = context.dataIndex
                const unitIndex = 1
                const units = context.chart.getDatasetMeta(unitIndex)?._parsed[itemIndex]?.y || 0
                let label = ''
                if (context.parsed.y !== null) {
                  label += new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                    context.parsed.y
                  )
                }
                return [label, `${units} units`]
              },
            },
          },
          legend: {
            display: false,
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            min: 0,
            suggestedMax: 5000000,
            ticks: {
              stepSize: 5000000,
              precision: 0,
              callback: function (value, index, ticks) {
                return value / 1000000
              },
            },
          },
          x: {
            ticks: {
              display: false,
            },
          },
        },
      },
    }
  },
  computed: {
    getCurrentLoanProductionData() {
      return this.loanProductionData[this.getCurrentPeriod] || {}
    },

    showPlaceholderUsers() {
      return !this.users.length || !this.officeYTD
    },

    getCurrentPeriod() {
      return this.periods[this.selectedPeriodIndex]
    },

    getYTDLabel() {
      if (this.selectedPeriodIndex === 0) {
        return 'YTD'
      }

      return this.getCurrentPeriod
    },

    canGoForward() {
      return this.periods.length > 1 && this.selectedPeriodIndex !== 0
    },

    canGoBack() {
      return (
        this.periods.length > 1 &&
        this.periods.length - 1 !== this.selectedPeriodIndex &&
        this.hasPeriodYTDByPeriodIndex(this.selectedPeriodIndex + 1)
      )
    },

    officeYTD() {
      const YTD = _.get(
        _.find(this.getCurrentLoanProductionData['Office - Total Volume'], { office_id: this.office.id }),
        'totalVolume',
        0
      )

      if (YTD) {
        const currencyFormatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        })
        return currencyFormatter.format(YTD)
      }

      return 0
    },

    orderedUsers() {
      if (this.showPlaceholderUsers) {
        return [{}, {}, {}, {}, {}]
      }

      return _.orderBy(this.getUsersWithLoanData(this.users), 'createdAt')
    },
    chartData() {
      if (this.showPlaceholderUsers) {
        return {
          labels: ['John Doe', 'Jane Doe', 'John Smith', 'Jane Smith', 'John Johnson'],
          datasets: [
            {
              label: `${this.getYTDLabel} Loan Production`,
              backgroundColor: '#147fc3',
              data: [5000000, 4000000, 3000000, 2000000, 100000],
              maxBarThickness: 40,
            },
          ],
        }
      }
      return {
        labels: this.orderedUsers.map((user) => user.name),
        datasets: [
          {
            label: `${this.getYTDLabel} Loan Production`,
            backgroundColor: '#147fc3',
            data: this.orderedUsers.map((user) => user.YTDLoanProduction),
            maxBarThickness: 40,
          },
          {
            label: 'Units',
            hidden: true,
            data: this.orderedUsers.map((user) => user.YTDLoanProductionUnits),
          },
        ],
      }
    },
  },
  watch: {
    loanProductionData() {
      this.resetPeriodIndex()
    },
  },
  created() {
    this.resetPeriodIndex()
  },
  methods: {
    resetPeriodIndex() {
      this.selectedPeriodIndex = 0
    },
    hasPeriodYTDByPeriodIndex(periodIndex) {
      if (!this.loanProductionData[this.periods[periodIndex]]?.['Office - Total Volume']) {
        return false
      }

      return !!_.get(
        _.find(this.loanProductionData[this.periods[periodIndex]]['Office - Total Volume'], {
          office_id: this.office.id,
        }),
        'totalVolume',
        0
      )
    },
    getUsersWithLoanData(users) {
      return users
        .map((user) => {
          const YTDLoanProduction =
            _.get(
              _.find(this.getCurrentLoanProductionData['LO - Total Volume'], { user_id: user.id }),
              'totalVolume',
              0
            ) ||
            _.get(
              _.find(this.getCurrentLoanProductionData['LO - Total Volume'], { nmls_number: user.nmlsNumber }),
              'totalVolume',
              0
            )

          const YTDLoanProductionUnits =
            _.get(
              _.find(this.getCurrentLoanProductionData['LO - Total Units'], { user_id: user.id }),
              'totalUnits',
              0
            ) ||
            _.get(
              _.find(this.getCurrentLoanProductionData['LO - Total Units'], { nmls_number: user.nmlsNumber }),
              'totalUnits',
              0
            )

          return {
            ...user,
            YTDLoanProduction,
            YTDLoanProductionUnits,
          }
        })
        .filter((user) => {
          if (user.isInactive && user.YTDLoanProduction === 0) {
            return false
          }

          if (user.isInactive && user.YTDLoanProduction !== 0) {
            return true
          }

          return user.isLoPhaseTwo
        })
    },
    goForward() {
      this.selectedPeriodIndex = this.selectedPeriodIndex - 1
    },
    goBack() {
      this.selectedPeriodIndex = this.selectedPeriodIndex + 1
    },
  },
}
</script>

<template>
  <div>
    <div :class="$style.headerContainer">
      <MottoIcon
        v-if="canGoBack"
        :class="$style.backButton"
        data-period-back-arrow
        name="Arrow"
        icon-color="gray"
        class="cursor-pointer"
        @click.native="goBack" />
      <div v-if="orderedUsers.length && officeYTD" class="mx-auto position-relative width-fit-content">
        <h1 class="color-primary-blue text-center mb-3 px-1"
          >{{ getYTDLabel }} Office: {{ officeYTD }}
          <Tooltip :target="`ytd-production-for-office`" placement="bottom" :icon-absolute-position="false">
            <span class="color-darkest-gray text-align-center overline-text font-weight-bold"
              >Production data is updated monthly when the Motto leaderboard is published.</span
            >
          </Tooltip></h1
        >
      </div>
      <MottoIcon
        v-if="canGoForward"
        :class="$style.forwardButton"
        data-period-forward-arrow
        name="Arrow"
        icon-color="gray"
        class="cursor-pointer"
        @click.native="goForward" />
    </div>
    <div :class="$style.graphContainer">
      <div v-if="!orderedUsers.length || showPlaceholderUsers" :class="$style.noUsersContainer">
        <p class="button-text color-dark-gray">Phase 2 LO production will be shown here.</p>
      </div>
      <Bar
        :class="$style.barGraphContainer"
        :chart-options="chartOptions"
        :plugins="chartPlugins"
        :chart-data="chartData" />
      <div :class="$style.profilePicturesContainer" :style="{ minHeight: `${barWidth}px` }">
        <div v-for="(user, i) in orderedUsers" :key="user.id">
          <ProfilePicture
            :id="user.id"
            :style="{ height: `${barWidth}px`, width: `${barWidth}px`, left: `${allBarPositions[i] - barWidth / 2}px` }"
            :class="$style.profilePicture"
            :src="user.photoUrl"
            :data-profile-picture-user-id="user.id"
            @click.native="$emit('click-user-picture', user)" />
          <b-tooltip
            v-if="!showPlaceholderUsers"
            :target="`${user.id}`"
            :placement="`bottom`"
            no-fade
            :custom-class="`tooltipGray light-transparent`">
            <p class="color-darkest-gray overline-text font-weight-bold">{{ user.name }}</p>
          </b-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.graphContainer {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.profilePicturesContainer {
  position: relative;
  width: 100%;
  margin-bottom: 10px;
}

.noUsersContainer {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  text-align: center;
  background: rgba($white, 0.5);
}

.profilePicture {
  position: absolute;
  cursor: pointer;
}

.barGraphContainer {
  flex-grow: 1;
}

.headerContainer {
  display: flex;
  justify-content: space-between;
}

.backButton {
  margin-top: 5px;

  > svg {
    width: 30px;
    height: 30px;
  }
}

.forwardButton {
  margin-top: 5px;

  > svg {
    width: 30px;
    height: 30px;
    transform: rotate(180deg);
  }
}
</style>
